import { apiFetch } from './client';

// AdminRole values mirror the CHECK constraint on core.org_membership.role.
// The owner row is special — present in the list but not editable from the
// administrators page.
export type AdminRole = 'owner' | 'administrator' | 'leader';

// One row in the unified administrators list. `status` is 'active' for
// accepted memberships and 'invited' for unclaimed invitations; the SPA
// renders both in the same table with a status pill.
export interface Administrator {
  status: 'active' | 'invited';
  user_id?: string;
  invitation_id?: string;
  email: string;
  display_name: string;
  role: AdminRole;
  team_ids: string[];
  invited_by?: string;
  created_at: string;
}

interface ListAdministratorsResponse {
  administrators: Administrator[];
}

export async function listAdministrators(
  token: string | null,
  slug: string,
): Promise<Administrator[]> {
  const res = await apiFetch<ListAdministratorsResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/administrators`,
    { token },
  );
  return res.administrators;
}

export interface InviteAdministratorInput {
  email: string;
  display_name: string;
  // Owner is filled at org-creation time and can't be invited via this path.
  role: 'administrator' | 'leader';
  // Required when role='leader'. Ignored when role='administrator'.
  team_ids?: string[];
}

export async function inviteAdministrator(
  token: string | null,
  slug: string,
  input: InviteAdministratorInput,
): Promise<Administrator> {
  return apiFetch<Administrator>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/administrators`,
    {
      method: 'POST',
      body: { ...input, team_ids: input.team_ids ?? [] },
      token,
    },
  );
}

export interface UpdateAdministratorInput {
  role: 'administrator' | 'leader';
  team_ids?: string[];
}

export async function updateAdministrator(
  token: string | null,
  slug: string,
  userID: string,
  input: UpdateAdministratorInput,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/administrators/${encodeURIComponent(userID)}`,
    {
      method: 'PATCH',
      body: { ...input, team_ids: input.team_ids ?? [] },
      token,
    },
  );
}

export async function removeAdministrator(
  token: string | null,
  slug: string,
  userID: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/administrators/${encodeURIComponent(userID)}`,
    { method: 'DELETE', token },
  );
}

export async function revokeInvitation(
  token: string | null,
  slug: string,
  invitationID: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/administrators/invitations/${encodeURIComponent(invitationID)}`,
    { method: 'DELETE', token },
  );
}

// Resend a pending invitation: refreshes its 30-day expiry server-side and
// re-fires the invitation email. Server returns 204 on success, 404 if the
// invitation has been accepted / revoked since the page last loaded.
export async function resendInvitation(
  token: string | null,
  slug: string,
  invitationID: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/administrators/invitations/${encodeURIComponent(invitationID)}/resend`,
    { method: 'POST', token },
  );
}

// Owner transfer mirrors the soft-delete typed-confirmation pattern: the SPA
// asks the user to type the org name, the server re-checks the match. Server
// also enforces that the caller is the current owner and the target already
// holds an active membership in the org.
export async function transferOwnership(
  token: string | null,
  slug: string,
  targetUserID: string,
  confirmName: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/administrators/${encodeURIComponent(targetUserID)}/transfer-owner`,
    {
      method: 'POST',
      body: { confirm_name: confirmName },
      token,
    },
  );
}
